/**
 * Friends management service
 */

import { apiGet, apiPost, apiDelete } from './api';
import type { SendFriendRequestRequest, PublicUserProfile } from '@/types';

/**
 * Friend request response from backend
 */
interface FriendRequestResponse {
  message?: string;
}

/**
 * Pending friend request with user details
 */
interface PendingFriendRequest {
  id: string;
  from_user: PublicUserProfile;
  created_at: string;
}

/**
 * Friends list response
 */
interface FriendsListResponse {
  friends: PublicUserProfile[];
}

/**
 * Get list of accepted friends
 */
export async function listFriends() {
  return apiGet<FriendsListResponse>('/api/friends/list');
}

/**
 * Get pending incoming friend requests
 */
export async function getPendingRequests() {
  return apiGet<PendingFriendRequest[]>('/api/friends/pending');
}

/**
 * Send a friend request to a user by email
 */
export async function sendFriendRequest(data: SendFriendRequestRequest) {
  return apiPost<FriendRequestResponse>('/api/friends/request', data);
}

/**
 * Send a friend request to a user by ID (for QR code scanning)
 */
export async function sendFriendRequestById(userId: string) {
  return apiPost<FriendRequestResponse>('/api/friends/request/user', { user_id: userId });
}

/**
 * Accept a pending friend request
 */
export async function acceptFriendRequest(requestId: string) {
  return apiPost<{ message: string }>(`/api/friends/requests/${requestId}/respond`, {
    action: 'accept'
  });
}

/**
 * Decline a pending friend request
 */
export async function declineFriendRequest(requestId: string) {
  return apiPost<{ message: string }>(`/api/friends/requests/${requestId}/respond`, {
    action: 'decline'
  });
}

/**
 * Block a user
 */
export async function blockUser(requestId: string) {
  return apiPost<{ message: string }>(`/api/friends/requests/${requestId}/respond`, {
    action: 'block'
  });
}

/**
 * Unfriend a user
 */
export async function unfriend(friendId: string) {
  return apiDelete<{ message: string }>(`/api/friends/friends/${friendId}`);
}
